import React from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '../../hooks/usePageMeta';
import { EMPRESA } from './empresa';
import './Legal.css';

const Termos: React.FC = () => {
  usePageMeta({
    title: 'Termos de Uso',
    description:
      'Termos de uso da plataforma UpdateCommit NFSe: cadastro, responsabilidades, planos, pagamentos e proteção de dados.',
    path: '/termos',
  });

  return (
    <div className="legal-page">
      <header className="legal-header">
        <h1 className="legal-title">Termos de Uso — UpdateCommit NFSe</h1>
        <p className="legal-meta">Versão 1.0 — Junho/2026 · Vigência: a partir de 01/07/2026</p>
      </header>

      <h2>1. Das partes</h2>
      <p>
        <strong>Prestadora:</strong> {EMPRESA.razaoSocial}, CNPJ {EMPRESA.cnpj}, com sede em{' '}
        {EMPRESA.endereco}, doravante denominada <strong>UpdateCommit</strong>.
      </p>
      <p>
        <strong>Contratante:</strong> pessoa física ou jurídica que realiza o cadastro na
        plataforma UpdateCommit NFSe, doravante denominada <strong>Usuário</strong>.
      </p>

      <h2>2. Do objeto</h2>
      <p>
        A UpdateCommit disponibiliza uma plataforma SaaS (Software as a Service) para emissão de
        Notas Fiscais de Serviços Eletrônicas (NFS-e) conforme o padrão NFS-e Nacional (LC 116 /
        SEFAZ Nacional v1.01), bem como serviços relacionados de gestão fiscal, integração via API
        e conciliação financeira.
      </p>

      <h2>3. Do cadastro e acesso</h2>
      <p>3.1. O uso da plataforma exige cadastro com dados verídicos e atualizados.</p>
      <p>3.2. O Usuário é responsável pela confidencialidade de suas credenciais de acesso.</p>
      <p>
        3.3. A UpdateCommit pode suspender contas que apresentem indício de uso indevido, fraude ou
        violação destes Termos.
      </p>

      <h2>4. Das responsabilidades do usuário</h2>
      <p>
        4.1. O Usuário é responsável pela corretude dos dados fiscais informados (CNPJ, Inscrição
        Municipal, código IBGE, descrição do serviço, valor, etc.).
      </p>
      <p>
        4.2. O Usuário é responsável pelo certificado digital utilizado, incluindo sua validade,
        guarda e senha.
      </p>
      <p>
        4.3. O Usuário compromete-se a utilizar a plataforma em conformidade com a legislação
        tributária vigente.
      </p>
      <p>
        4.4. A emissão de notas fiscais com dados falsos ou para fins de simulação de
        receita/despesa é vedada e pode acarretar responsabilidade civil e criminal.
      </p>

      <h2>5. Das responsabilidades da UpdateCommit</h2>
      <p>
        5.1. A UpdateCommit compromete-se a manter a plataforma disponível, com meta de
        disponibilidade de 99,5% mensais (exceto janelas de manutenção programada).
      </p>
      <p>
        5.2. A UpdateCommit não é responsável por indisponibilidade ou rejeição de notas causada
        por instabilidade da SEFAZ Nacional ou dos sistemas da prefeitura competente.
      </p>
      <p>
        5.3. A UpdateCommit não é responsável por penalidades fiscais decorrentes de erros nos
        dados informados pelo Usuário.
      </p>

      <h2>6. Do certificado digital</h2>
      <p>
        6.1. O certificado PFX e a senha são criptografados com AES-256-GCM antes de serem
        armazenados. Nenhum colaborador da UpdateCommit tem acesso ao conteúdo descriptografado.
      </p>
      <p>
        6.2. O Usuário concede à UpdateCommit licença limitada para usar o certificado
        exclusivamente para assinar XMLs e estabelecer conexões mTLS com a SEFAZ em nome do
        Usuário.
      </p>

      <h2>7. Das restrições de uso</h2>
      <p>É vedado ao Usuário:</p>
      <ul>
        <li>Usar a plataforma para emitir notas com dados falsos;</li>
        <li>Tentar acessar dados de outras organizações;</li>
        <li>Realizar engenharia reversa, decompilação ou tentativa de acesso não autorizado;</li>
        <li>Revender acesso à plataforma sem contrato específico;</li>
        <li>Exceder os limites do plano contratado por meios técnicos.</li>
      </ul>

      <h2>8. Dos planos e pagamentos</h2>
      <p>
        8.1. O acesso aos planos pagos está sujeito ao pagamento das mensalidades ou anuidades
        conforme os valores exibidos na plataforma.
      </p>
      <p>
        8.2. O cancelamento da assinatura pode ser feito a qualquer momento pelo portal de
        cobrança. O acesso continua até o fim do período já pago.
      </p>
      <p>
        8.3. A UpdateCommit pode alterar os preços com aviso prévio de 30 dias. Usuários com
        assinatura ativa mantêm o preço contratado até o próximo ciclo de renovação.
      </p>
      <p>
        8.4. O pagamento é processado pela Stripe, Inc. Os dados de cartão não passam pelos
        servidores da UpdateCommit.
      </p>

      <h2>9. Da privacidade e proteção de dados</h2>
      <p>
        O tratamento de dados pessoais é regulado pela{' '}
        <Link to="/privacidade">Política de Privacidade</Link>. A UpdateCommit está comprometida
        com a conformidade à Lei Geral de Proteção de Dados (LGPD — Lei 13.709/2018).
      </p>

      <h2>10. Da vigência e rescisão</h2>
      <p>10.1. Estes Termos entram em vigor no momento do cadastro.</p>
      <p>
        10.2. A UpdateCommit pode encerrar a conta do Usuário mediante aviso prévio de 30 dias,
        salvo em casos de violação grave, nos quais o encerramento pode ser imediato.
      </p>
      <p>
        10.3. Ao encerrar a conta, os dados ficam retidos por 90 dias para eventual solicitação de
        exportação, após os quais são excluídos permanentemente.
      </p>

      <h2>11. Das disposições gerais</h2>
      <p>11.1. Estes Termos são regidos pela legislação brasileira.</p>
      <p>11.2. Fica eleito o Foro da {EMPRESA.foro} para dirimir eventuais disputas.</p>
      <p>11.3. A invalidade de qualquer cláusula não afeta as demais.</p>

      <p className="legal-footer-note">
        Dúvidas: <a href={`mailto:${EMPRESA.emailContato}`}>{EMPRESA.emailContato}</a>
      </p>
    </div>
  );
};

export default Termos;
